import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";
import { Schemas } from "../types/api-types";

type CustomerResponse = Schemas['CustomerResponse'];
type PlAccount = Schemas['PlAccountResponse'];

export async function getCustomerByNumber(customerNumber: string): Promise<CustomerResponse> {
    try {
        const response: ApiResponse<CustomerResponse> = (
            await apiClient.get(`/customer/${customerNumber}`)
        ).data;
        return response.data;

    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || error.response.data.message || 'Fetching customer failed');
        }
        if (error.request) {
            throw new Error('Network error: server did not respond. Please try again or contact support team.');
        }
        throw new Error(error.message || 'Fetching customer failed');
    }
}

export async function getCustomerAccountsById(customerId: number, showDormant: boolean = false): Promise<PlAccount[]> {
    try {
        const response: ApiResponse<PlAccount[]> = (
            await apiClient.get(`/customer/${customerId}/accounts`, { params: { show_dormant: showDormant } })
        ).data;

        return response.data;

    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching customer failed');
        } 
        throw new Error('network error, please contact support team');
    }
}

export async function getCustomerAllAccountsById(customerId: number, showDormant: boolean = false, showInactive: boolean = false): Promise<PlAccount[]> {
    try {
        const response: ApiResponse<PlAccount[]> = (
            await apiClient.get(`/customer/${customerId}/all_accounts`, { params: { show_dormant: showDormant, show_inactive: showInactive } })
        ).data;

        return response.data;

    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching customer failed');
        }
        throw new Error('network error, please contact support team');
    }
}

type CustomerListDetail = Schemas['CustomerListDetail'];

export async function searchCustomers(
    searchTerm: string,
    branchId?: number
): Promise<CustomerListDetail[]> {
    try {
        const params: Record<string, any> = { customer_number: searchTerm, page: 1, page_size: 20 };
        if (branchId) params.branch_id = branchId;
        const response: ApiResponse<Schemas['PaginatedCustomerListResponse']> = (
            await apiClient.get('/customer/', { params })
        ).data;
        return response.data?.items ?? [];
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Customer search failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getCustomerSavingAccountsById(customerId: number, balanceFilter: boolean = true): Promise<PlAccount[]> {
    try {
        const response: ApiResponse<PlAccount[]> = (
            await apiClient.get(`/customer/${customerId}/saving_accounts`, { params: { balance_filter: balanceFilter } })
        ).data;
        return response.data;

    }
    catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching customer saving accounts failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getCustomerSavingAccountsForFdClosureById(customerId: number, balanceFilter: boolean = true): Promise<PlAccount[]> {
    try {
        const response: ApiResponse<PlAccount[]> = (
            await apiClient.get(`/customer/${customerId}/saving_accounts_for_fd_closure`, { params: { balance_filter: balanceFilter } })
        ).data;
        return response.data;

    }
    catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching customer saving accounts failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getCustomerFdAccountsById(customerId: number): Promise<PlAccount[]> {
    try {
        const response: ApiResponse<PlAccount[]> = (
            await apiClient.get(`/customer/${customerId}/fd_accounts`)
        ).data;
        return response.data;

    }
    catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching customer saving accounts failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getCustomerCreditorsAccountsById(customerId: number): Promise<PlAccount[]> {
    try {
        const response: ApiResponse<PlAccount[]> = (
            await apiClient.get(`/customer/${customerId}/creditor_accounts`)
        ).data;
        return response.data;

    }
    catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching customer creditor accounts failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getCustomerDebtorAccountsById(customerId: number): Promise<PlAccount[]> {
    try {
        const response: ApiResponse<PlAccount[]> = (
            await apiClient.get(`/customer/${customerId}/debtor_accounts`)
        ).data;
        return response.data;

    }
    catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching customer debtor accounts failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export type DebtorProduct = {
    id?: number | null;
    pl_account_id?: number | null;
    name?: string | null;
    description?: string | null;
    unit_price?: number | null;
    qty?: number | null;
    amount?: number | null;
    status?: number | null;
    c_at?: string | null;
};

export async function getDebtorProductsByAccountId(plAccountId: number): Promise<DebtorProduct[]> {
    try {
        const response: ApiResponse<DebtorProduct[]> = (
            await apiClient.get(`/customer/debtor-products/${plAccountId}`)
        ).data;
        return response.data || [];

    }
    catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching debtor products failed');
        }
        throw new Error('network error, please contact support team');
    }
}